import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogPreventiveCare = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="/images/blog1.png"
          alt="How to Choose the Right Doctor"
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&q=80'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#39a5d3] text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">
              PREVENTIVE CARE
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              How to Choose the Right Doctor for Your Health Needs
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />15 Mar 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />6 min read</span>
              <span>•</span>
              <span>Dr. Anjali Rao</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">How to Choose the Right Doctor</span>
        </div>
      </div>

      {/* SECTION 1: 2-COLUMN WITH SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Main Content */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">
              {/* Intro Quote */}
              <p className="text-lg leading-relaxed mb-8 border-l-4 border-[#39a5d3] pl-5 bg-blue-50 py-4 pr-4 rounded-r-lg">
                Choosing the right doctor is one of the most important health decisions you'll ever make — yet most people spend more time researching a restaurant than selecting their physician. Whether you need a specialist for a chronic condition or a trusted general practitioner for routine care, finding the right match can mean the difference between getting better and staying stuck in a cycle of ineffective treatment. With hundreds of doctors listed on platforms like Docmize, knowing what to look for can feel overwhelming. This guide will walk you through exactly what matters, what doesn't, and how to make a confident, informed decision for yourself and your family.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">
                Why Choosing the Right Doctor Matters
              </h2>
              <p className="mb-4">
                The doctor-patient relationship is one of the most consequential relationships in your life. A good doctor doesn't just treat your symptoms — they understand your health history, lifestyle, risk factors, and goals. They communicate clearly, listen carefully, and partner with you to make decisions rather than simply issuing orders. Research consistently shows that patients who feel a strong connection with their doctor are more likely to follow treatment plans, attend follow-up appointments, and achieve better health outcomes overall.
              </p>
              <p className="mb-8">
                Conversely, a poor doctor-patient match can lead to misdiagnosis, unnecessary tests, delayed treatment, and even medical errors. Many people continue seeing doctors they're uncomfortable with simply because changing feels complicated. But with platforms like Docmize making it easy to browse verified doctor profiles, read patient reviews, and book appointments online, there's no reason to settle for less than the right fit. Your health deserves that investment of time and attention.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">
                Step 1 — Identify What Type of Doctor You Need
              </h2>
              <p className="mb-4">
                Before you start searching, be clear about what kind of care you're looking for. The medical world is divided into primary care and specialist care, and understanding the difference will save you significant time and money.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { t: 'General Practitioner (GP) / Family Doctor', d: 'Your first point of contact for most health concerns. GPs manage a wide range of conditions, provide preventive care and vaccinations, and refer you to specialists when needed. Everyone should have a trusted GP — they become familiar with your complete health history over time, which is invaluable for coordinated care.' },
                  { t: 'Internist (General Internal Medicine)', d: 'Specialists in adult medicine who manage complex, chronic conditions like diabetes, hypertension, and heart disease. More specialized than a GP but less so than organ-specific specialists. Excellent choice for adults with multiple conditions.' },
                  { t: 'Specialist', d: 'When you have a specific condition requiring expert knowledge — a cardiologist for heart issues, a dermatologist for skin conditions, an orthopedic surgeon for joint problems. Usually requires a referral from your GP, though many platforms like Docmize allow direct specialist booking.' },
                  { t: 'Surgeon', d: 'If your condition may require a surgical procedure, you\'ll need a surgeon who specializes in that area. Always get a second opinion before elective surgery, and ensure the surgeon performs your specific procedure frequently — experience matters enormously in surgical outcomes.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

            </div>

            {/* Sidebar */}
            <aside className="space-y-7">

              {/* Key Facts */}
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'People change doctors each year', v: '30%+' },
                    { l: 'Patients with trusted doctor', v: 'Better outcomes' },
                    { l: 'Avg. time spent choosing doctor', v: 'Under 5 mins' },
                    { l: 'Patients who research doctors', v: '77% Online' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Find Your Doctor</h3>
                <p className="text-white/80 text-xs mb-4">Browse verified specialists on Docmize today.</p>
                <Link to="/doctors" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Browse Doctors
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>

            </aside>
          </div>
        </div>
      </section>

      {/* SECTION 2: FULL WIDTH */}
      <section className="pb-14">
        <div className="max-w-6xl mx-auto px-6">
          <article className="text-gray-700 leading-relaxed">

            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">
              Step 2 — Check Qualifications and Credentials
            </h2>
            <p className="mb-5">In India, medical qualifications are regulated by the National Medical Commission (NMC). Here's what to look for when evaluating a doctor's credentials:</p>
            <div className="space-y-3 mb-10">
              {[
                { t: 'MBBS (Bachelor of Medicine, Bachelor of Surgery)', d: 'The basic medical degree in India. All doctors must have this to practice. A 5.5-year undergraduate medical degree followed by a 1-year internship.' },
                { t: 'MD / MS (Postgraduate Degree)', d: 'MD (Doctor of Medicine) for non-surgical specialties, MS (Master of Surgery) for surgical ones. These 3-year postgraduate degrees indicate specialist training. Always check that a specialist has the relevant postgraduate degree.' },
                { t: 'DM / MCh (Super-Specialty)', d: 'The highest level of specialist training in India — a 3-year fellowship after MD/MS. A cardiologist with DM Cardiology, for example, has undergone the most rigorous training available in their field.' },
                { t: 'DNB (Diplomate of National Board)', d: 'An alternative postgraduate qualification offered by the National Board of Examinations — equivalent to MD/MS and fully recognized. Many excellent specialists hold DNB qualifications.' },
                { t: 'Fellowship (FRCS, FACC, etc.)', d: 'International fellowships and certifications indicate advanced training or recognition from professional bodies. A cardiologist with FACC (Fellow of the American College of Cardiology) has met international standards of excellence.' },
              ].map((c, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <p className="font-bold text-gray-900 mb-1.5">{c.t}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>

            {/* MID ARTICLE IMAGE */}
            <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <img
                  src="/images/blog1.png"
                  alt="Doctor consultation"
                  className="w-full h-64 object-cover"
                  onError={e => { e.target.src = '/images/11.png'; }}
                />
                <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                  <p className="text-xs text-gray-500 italic text-center">A good doctor-patient relationship is built on trust, communication, and respect.</p>
                </div>
              </div>
              <div className="text-gray-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Step 3 — Evaluate Experience and Specialization</h3>
                <p className="text-sm leading-relaxed mb-3">
                  Qualifications tell you what a doctor studied. Experience tells you how much they've actually done. For complex conditions or surgical procedures, experience matters enormously — a surgeon who performs 200 knee replacements a year will have significantly better outcomes than one who performs 20.
                </p>
                <p className="text-sm leading-relaxed mb-3">
                  Look for doctors who subspecialize within their specialty if you have a specific condition. A cardiologist who focuses primarily on heart failure management will be better suited for a patient with advanced heart failure than a general cardiologist.
                </p>
                <p className="text-sm leading-relaxed">
                  On Docmize, every doctor profile lists their years of experience, specific areas of expertise, and the conditions they treat most frequently — making it easy to find someone who genuinely specializes in exactly what you need.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">
              Step 4 — Read Patient Reviews Carefully
            </h2>
            <p className="mb-5">Patient reviews are valuable but need to be interpreted carefully. Here's how to get the most out of them:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
              {[
                'Look for patterns across multiple reviews, not just one or two',
                'Pay attention to comments about communication style and listening',
                'Check reviews mentioning your specific condition or procedure',
                'Note how the doctor or clinic responds to negative feedback',
                'Volume matters — 500 reviews at 4.7 is more reliable than 10 at 5.0',
                'Look for mention of wait times and appointment availability',
                'Check if patients mention being rushed or feeling heard',
                'Recent reviews (last 6 months) are more relevant than older ones',
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
                  <span className="w-2 h-2 rounded-full bg-[#39a5d3] mt-1.5 flex-shrink-0" />
                  <p className="text-sm">{s}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">
              Step 5 — Consider Practical Factors
            </h2>
            <p className="mb-6">Even the most qualified, highly-reviewed doctor isn't right for you if practical barriers prevent you from seeing them consistently. Consider these factors:</p>
            <div className="space-y-4 mb-10">
              {[
                { n: '01', t: 'Location and Accessibility', d: 'A clinic that\'s close to your home or workplace makes it far easier to attend appointments, especially for ongoing conditions. Consider traffic, parking, and public transport options. For routine care, convenience drives consistency — and consistency drives better health outcomes.' },
                { n: '02', t: 'Appointment Availability', d: 'How quickly can you get an appointment? Some highly sought-after specialists have waiting times of weeks or months. For chronic conditions requiring regular monitoring, check whether the doctor has good slot availability. Docmize shows real-time availability so you can see exactly when a doctor can see you.' },
                { n: '03', t: 'Consultation Fee', d: 'Consultation fees in India range from ₹200 for a GP to ₹2000+ for senior specialists. More expensive doesn\'t necessarily mean better — many excellent doctors practice at very reasonable rates. Check consultation fees upfront to avoid surprises, and factor in the cost of follow-up visits.' },
                { n: '04', t: 'Languages Spoken', d: 'You should be able to communicate comfortably with your doctor. If Hindi, Tamil, Kannada, or another regional language is your primary language and you struggle with English, find a doctor who speaks your language — miscommunication in medical settings can have serious consequences.' },
                { n: '05', t: 'Hospital Affiliation', d: 'If your condition might eventually require hospitalization or surgery, consider which hospital your doctor is affiliated with. A doctor affiliated with a reputable, well-equipped hospital gives you continuity of care if you need to be admitted.' },
              ].map(item => (
                <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                  <div className="bg-[#19354a] text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">
              Red Flags to Watch Out For
            </h2>
            <p className="mb-5">Just as important as knowing what to look for is knowing when to walk away. These are warning signs that a doctor may not be right for you:</p>
            <div className="space-y-2.5 mb-10">
              {[
                'Dismisses your concerns or makes you feel rushed during consultations',
                'Can\'t clearly explain your diagnosis or treatment plan in terms you understand',
                'Prescribes medications without explaining what they are or why you need them',
                'Discourages you from seeking a second opinion',
                'Seems more interested in upselling tests and procedures than addressing your concerns',
                'Has no online presence, reviews, or verifiable credentials',
                'Clinic is consistently disorganized, unhygienic, or staff are rude',
                'Promises guaranteed cures or outcomes — no ethical doctor makes such promises',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">✕</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>

            {/* Summary Box */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-7 mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Finding Your Doctor on Docmize</h2>
              <p className="text-sm mb-4 leading-relaxed text-gray-600">Docmize makes it simple to apply all these criteria at once. Here's what you can do on our platform:</p>
              <ul className="space-y-2">
                {[
                  'Filter doctors by specialty, city, experience, rating, and consultation fee',
                  'Read verified patient reviews from real appointments',
                  'View complete doctor profiles with qualifications, specializations, and languages spoken',
                  'Check real-time availability and book confirmed appointments instantly',
                  'No booking fee — you pay only when you visit the clinic',
                  'Available across Bangalore, Delhi, Mumbai, Gurgaon, and Noida',
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#39a5d3] mt-2 flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                <ChevronLeft size={16} /> Back to All Blogs
              </Link>
             
            </div>

          </article>
        </div>
      </section>

    </div>
  );
};

export default BlogPreventiveCare;